// ── BCHNostr — Creator channel verification ───────────────────────
//
// The video system lets anyone post a link to a PUBLIC YouTube video —
// including videos they didn't make. That is fine (BCHNostr embeds, never
// hosts), but a viewer can't tell "this is the creator" from "someone
// shared this". Verification closes that gap WITHOUT adding friction to
// posting: labels are derived automatically at render time, and a creator
// proves channel control ONCE, not per video.
//
// The proof is a classic control challenge, the same shape as NIP-05 domain
// verification: the app hands the creator a token that only the channel
// owner could place where we then read it back — the description of one of
// their own YouTube videos. If the token is present, we publish a signed,
// per-channel verification event. Every YouTube video the creator posts
// from that channel then shows a "Verified creator" badge; everything else
// shows a soft "Shared" tag. No per-video prompt, ever.
//
// TRUST: a verification event is a signed CLAIM by the poster about their
// own channel. It is only meaningful for videos posted by that same pubkey,
// and consumers must author-filter (a badge from event author A can never
// vouch for a video by author B). The channel-control fetch is best-effort
// and read-only, through the shared CORS proxy.

import type { Event } from 'nostr-tools';
import { nip19 } from 'nostr-tools';
import { parseVideoUrl, resolveVideoUrl } from './videoProviders';

// ── Kind & tags ───────────────────────────────────────────────────

/** Per-channel creator verification. Addressable, one per channel per user. */
export const CREATOR_VERIFY_KIND = 30078;
/** d-tag: one verification per (user, platform, channel). */
export const CREATOR_VERIFY_D_PREFIX = 'bchnostr/creator-verify/';
/** Relay-indexable discovery tag. */
export const CREATOR_VERIFY_TAG = 'bch-creator-verify';

/** Providers that support the description-token control challenge. */
export type VerifyPlatform = 'youtube' | 'odysee';

/** Human label for a verifiable platform. */
export function verifyPlatformLabel(platform: VerifyPlatform): string {
  return platform === 'odysee' ? 'Odysee' : 'YouTube';
}

/** Map a resolved video provider to a verifiable platform, or null. */
export function verifyPlatformForProvider(provider: string): VerifyPlatform | null {
  if (provider === 'youtube') return 'youtube';
  if (provider === 'odysee') return 'odysee';
  return null;
}

// ── Auto-derived label & tip-gating policy ────────────────────────

/**
 * Per-video verification label:
 *   'verified' — a video whose channel the poster has proven they control,
 *   'shared'   — a verifiable-platform video with no matching verification,
 *   'unknown'  — not applicable (non-verifiable provider) OR channel not yet
 *                resolved (transient, only while a lookup is in flight).
 */
export type VerificationLabel = 'verified' | 'shared' | 'unknown';

/**
 * Whether a video may receive tips, and — when it may not — why.
 *
 *   'eligible' — tips allowed.
 *   'checking' — a verifiable-platform video whose verification is still
 *                resolving; hold the button in a neutral "checking" state
 *                rather than enable or hard-block it.
 *   'unverified' — a YouTube/Odysee video the poster has NOT proven they
 *                created. Tips are disabled so nobody farms tips off a video
 *                they merely shared.
 */
export type TipEligibility = 'eligible' | 'checking' | 'unverified';

/**
 * Decide whether a video may be tipped, given its provider and its
 * auto-derived verification label. This is the single source of truth for
 * tip gating across every video surface.
 *
 * Policy:
 *   · YouTube / Odysee → tippable ONLY when 'verified'. 'shared' is blocked
 *     ('unverified'); a still-resolving label is 'checking'.
 *   · Nostr-native uploads (nostrmedia / Blossom / NIP-96) → always tippable:
 *     the blob is published under the poster's own key, so they are provably
 *     the creator — there is no external channel to freeload from.
 *   · Every other provider (PeerTube, direct files, external) → tippable.
 *     No control challenge exists for them yet, so blocking would punish
 *     genuine self-hosting creators with no way to prove themselves. This is
 *     the one lever to tighten if those providers gain verification later.
 */
export function videoTipEligibility(
  provider: string, label: VerificationLabel,
): TipEligibility {
  // Verifiable platforms: verification is required.
  if (verifyPlatformForProvider(provider)) {
    if (label === 'verified') return 'eligible';
    if (label === 'shared') return 'unverified';
    return 'checking'; // 'unknown' → still resolving
  }
  // Nostr-native uploads and every other provider: allowed.
  return 'eligible';
}

export interface CreatorVerification {
  /** The Nostr pubkey that proved control. */
  pubkey: string;
  platform: VerifyPlatform;
  /** Canonical channel id we bind to (a YouTube handle "@name" or "UC…"). */
  channel: string;
  /** When the proof was recorded. */
  verifiedAt: number;
  event: Event;
}

// ── Token ─────────────────────────────────────────────────────────

/**
 * The token a creator pastes into one of their video descriptions. It is
 * derived from their npub so it is unique to them and can't be reused by
 * someone else to claim the same channel. Not a secret — it only proves
 * that whoever placed it can edit that channel's content.
 */
export function verificationToken(pubkey: string): string {
  let npub = pubkey;
  try { npub = nip19.npubEncode(pubkey); } catch { /* keep hex on failure */ }
  return `bchnostr-verify:${npub}`;
}

// ── Channel identity ──────────────────────────────────────────────
//
// Each platform names channels differently, but the verification system
// needs a single normalized key per channel that is:
//   · stable  — the same channel always maps to the same key,
//   · safe    — usable in a `d` tag and a `t`/`#a` filter,
//   · a trust binding — it identifies exactly ONE channel, so a squatter
//     who registers the same display handle cannot match.
//
// YouTube: a handle ("@name") or a "UC…" id, lowercased.
// Odysee : "@name#<40-hex-claim-id>" — the claim id is what makes it a
//   trust binding. Odysee lets two accounts register the same @name with
//   different claim ids, so the handle ALONE is not identity; the channel
//   claim id is. We therefore always bind to the full claim id.

const HANDLE_RE = /^@[a-z0-9._-]{1,60}$/i;
const CHANNEL_ID_RE = /^UC[a-zA-Z0-9_-]{20,24}$/;
const ODYSEE_CLAIM_ID_RE = /^[0-9a-f]{40}$/i;
const ODYSEE_CHANNEL_RE = /^@[a-z0-9._@-]{1,120}#[0-9a-f]{40}$/;

/**
 * Normalize a YouTube channel identifier, or '' if it isn't a plausible one.
 * Kept as the default `normalizeChannel` for backward compatibility with
 * existing verification events (all of which are YouTube).
 */
export function normalizeChannel(raw: string | undefined | null): string {
  if (!raw || typeof raw !== 'string') return '';
  let v = raw.trim();
  // Accept a full author_url too: https://www.youtube.com/@handle or /channel/UC…
  const asUrl = parseVideoUrl(v);
  if (asUrl) {
    const parts = asUrl.pathname.split('/').filter(Boolean);
    const chIdx = parts.indexOf('channel');
    if (chIdx >= 0 && parts[chIdx + 1]) v = parts[chIdx + 1];
    else if (parts[0]?.startsWith('@')) v = parts[0];
    else v = parts[parts.length - 1] || v;
  }
  if (CHANNEL_ID_RE.test(v)) return v; // keep UC… case-sensitive
  if (!v.startsWith('@')) v = '@' + v;
  return HANDLE_RE.test(v) ? v.toLowerCase() : '';
}

/**
 * Build the canonical Odysee channel key from a channel name and its full
 * claim id: "@name#<claimid>". Returns '' if either part is implausible.
 * The claim id is the trust binding — the handle alone is not identity.
 */
export function odyseeChannelKey(name: string | undefined, claimId: string | undefined): string {
  if (!name || !claimId || !ODYSEE_CLAIM_ID_RE.test(claimId)) return '';
  let handle = name.trim();
  if (!handle.startsWith('@')) handle = '@' + handle;
  handle = handle.toLowerCase();
  // Reject a handle carrying anything that could break the `#claimid` divider
  // or a `d`-tag path segment.
  if (!/^@[a-z0-9._@-]{1,120}$/.test(handle)) return '';
  const key = `${handle}#${claimId.toLowerCase()}`;
  return ODYSEE_CHANNEL_RE.test(key) ? key : '';
}

/**
 * A short, human-friendly rendering of a normalized channel key. For Odysee
 * this drops the long claim-id suffix ("@name#<40hex>" → "@name"); for
 * YouTube the key is already readable. Never used for comparison — only
 * display — since the claim id is what makes Odysee identity unambiguous.
 */
export function displayChannel(channel: string | undefined | null): string {
  if (!channel) return '';
  const hash = channel.indexOf('#');
  return hash > 0 ? channel.slice(0, hash) : channel;
}

/**
 * Normalize a channel identifier for the given platform. Returns '' when the
 * value isn't a plausible channel for that platform.
 */
export function normalizeChannelFor(
  platform: VerifyPlatform, raw: string | undefined | null,
): string {
  if (platform === 'odysee') {
    if (!raw || typeof raw !== 'string') return '';
    const v = raw.trim();
    return ODYSEE_CHANNEL_RE.test(v.toLowerCase()) ? v.toLowerCase() : '';
  }
  return normalizeChannel(raw);
}

// ── Events ────────────────────────────────────────────────────────

export function creatorVerifyDTag(platform: VerifyPlatform, channel: string): string {
  return `${CREATOR_VERIFY_D_PREFIX}${platform}/${channel}`;
}

/** Build the signed verification event (published by the verified creator). */
export function buildCreatorVerifyEvent(
  platform: VerifyPlatform, channel: string,
): { kind: number; tags: string[][]; content: string } {
  const norm = normalizeChannelFor(platform, channel);
  if (!norm) {
    throw new Error(platform === 'odysee'
      ? 'Unrecognized Odysee channel — could not read its channel claim id.'
      : 'Unrecognized channel — expected a YouTube @handle or channel URL.');
  }
  const tags: string[][] = [
    ['d', creatorVerifyDTag(platform, norm)],
    ['t', CREATOR_VERIFY_TAG],
    ['t', `bch-verify-${platform}`],
    ['channel', norm],
    ['platform', platform],
    ['alt', `Verified ${verifyPlatformLabel(platform)} creator on BCHNostr`],
  ];
  return { kind: CREATOR_VERIFY_KIND, tags, content: JSON.stringify({ platform, channel: norm }) };
}

/** Parse a verification event into a CreatorVerification, or null. */
export function parseCreatorVerify(ev: Event | null | undefined): CreatorVerification | null {
  if (!ev || ev.kind !== CREATOR_VERIFY_KIND) return null;
  const dTag = ev.tags.find(t => t[0] === 'd')?.[1] || '';
  if (!dTag.startsWith(CREATOR_VERIFY_D_PREFIX)) return null;
  if (!ev.tags.some(t => t[0] === 't' && t[1] === CREATOR_VERIFY_TAG)) return null;
  // Empty content is a retraction (creator removed the verification).
  if (!ev.content.trim()) return null;

  // Platform is whatever the event declares, defaulting to youtube for the
  // legacy events that predate multi-platform support (they carry no explicit
  // platform tag in the very first releases, though current ones do).
  const platformTag = ev.tags.find(t => t[0] === 'platform')?.[1];
  const platform: VerifyPlatform = platformTag === 'odysee' ? 'odysee' : 'youtube';
  const channel = normalizeChannelFor(platform, ev.tags.find(t => t[0] === 'channel')?.[1]);
  if (!channel) return null;

  return { pubkey: ev.pubkey, platform, channel, verifiedAt: ev.created_at, event: ev };
}

// ── Channel resolution for a posted video ─────────────────────────
//
// To label a video we need its channel, which the video event does not
// store. We resolve it lazily from the provider and cache the result per
// (provider, id) in localStorage, so a feed of the same creator's videos
// costs at most one lookup per distinct video and none on repeat visits.
//
//   YouTube — oEmbed `author_url` gives the channel handle/UC id.
//   Odysee  — the JSON-RPC `resolve` API gives the SIGNING CHANNEL's name
//             and full 40-hex claim id, which we bind to as "@name#claimid".
//             The claim id (not the handle) is the trust binding.
//
// All fetches go through the shared read-only CORS proxy. Best-effort and
// never throwing: a '' result is treated as "unknown", never as a match.

const CHANNEL_CACHE_PREFIX = 'bchnostr:vid-channel:';
const PROXY = 'https://proxy.shakespeare.diy/?url=';
const inFlight = new Map<string, Promise<string>>();

function cacheGet(key: string): string | null {
  try { return localStorage.getItem(CHANNEL_CACHE_PREFIX + key); } catch { return null; }
}
function cacheSet(key: string, channel: string): void {
  try { localStorage.setItem(CHANNEL_CACHE_PREFIX + key, channel); } catch { /* ignore */ }
}

/** De-duplicate concurrent lookups and cache the resolved channel key. */
function resolveCached(cacheKey: string, work: () => Promise<string>): Promise<string> {
  const cached = cacheGet(cacheKey);
  if (cached !== null) return Promise.resolve(cached);

  const existing = inFlight.get(cacheKey);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const channel = await work();
      // Cache even '' so we don't refetch a video whose channel we can't read.
      cacheSet(cacheKey, channel);
      return channel;
    } catch {
      return '';
    } finally {
      inFlight.delete(cacheKey);
    }
  })();

  inFlight.set(cacheKey, promise);
  return promise;
}

/**
 * Resolve the YouTube channel (@handle or UC…) for a video id, cached.
 * Returns '' when it can't be determined.
 */
export async function resolveYouTubeChannel(videoId: string): Promise<string> {
  if (!/^[A-Za-z0-9_-]{11}$/.test(videoId)) return '';
  return resolveCached(`youtube:${videoId}`, async () => {
    const source = `https://www.youtube.com/watch?v=${videoId}`;
    const oembed = `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(source)}`;
    const res = await fetch(`${PROXY}${encodeURIComponent(oembed)}`, {
      signal: AbortSignal.timeout(10000),
    });
    if (!res.ok) return '';
    const data: unknown = await res.json();
    if (!data || typeof data !== 'object') return '';
    const authorUrl = (data as Record<string, unknown>).author_url;
    return normalizeChannel(typeof authorUrl === 'string' ? authorUrl : '');
  });
}

// ── Odysee resolution via the LBRY SDK `resolve` proxy ────────────
//
// Odysee's frontend resolves claims through a public JSON-RPC proxy. We use
// the same endpoint to read a video claim's `signing_channel` (its name and
// full claim id) and its `value.description`. This is far more robust than
// scraping a watch page, and gives us the claim id we bind identity to.

const ODYSEE_RESOLVE_API = 'https://api.na-backend.odysee.com/api/v1/proxy?m=resolve';

interface OdyseeClaim {
  description: string;
  channelName: string;
  channelClaimId: string;
  /** Whether the video's signature by its channel is valid (spoof guard). */
  signatureValid: boolean;
}

/**
 * The Odysee provider stores its claim path with each segment
 * percent-encoded (see `encodeOdyseeSegment` in videoProviders), but the LBRY
 * `resolve` API wants the DECODED claim name (`we're-…-(for`, not
 * `we%27re-…-%28for`). Decode each `/`-separated segment while preserving the
 * structural `@` and `:` dividers.
 */
function decodeOdyseeClaimPath(claimPath: string): string {
  return claimPath
    .split('/')
    .map(seg => { try { return decodeURIComponent(seg); } catch { return seg; } })
    .join('/');
}

/**
 * Resolve a single Odysee claim (by its claim path, e.g. "@name:c/video:d")
 * to its description and signing-channel identity. Returns null on any
 * failure. Never throws.
 */
async function resolveOdyseeClaim(claimPath: string): Promise<OdyseeClaim | null> {
  if (!claimPath || claimPath.length > 400) return null;
  const decoded = decodeOdyseeClaimPath(claimPath);
  // Real Odysee claim names legitimately contain apostrophes, parens and
  // commas, so we only reject genuine control/whitespace characters. The
  // value is placed inside a JSON string via JSON.stringify (fully escaped)
  // and read only for its channel identity + description — never rendered as
  // markup — so ordinary punctuation is safe.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\s]/.test(decoded)) return null;

  const body = JSON.stringify({
    jsonrpc: '2.0', method: 'resolve', id: 1,
    params: { urls: [decoded] },
  });
  const res = await fetch(`${PROXY}${encodeURIComponent(ODYSEE_RESOLVE_API)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    signal: AbortSignal.timeout(15000),
  });
  if (!res.ok) return null;

  const payload: unknown = await res.json();
  const result = (payload as Record<string, unknown> | null)?.result;
  if (!result || typeof result !== 'object') return null;

  // The result is keyed by the requested url; take the first entry.
  const entry = Object.values(result as Record<string, unknown>)[0];
  if (!entry || typeof entry !== 'object') return null;
  const claim = entry as Record<string, unknown>;
  if ('error' in claim) return null;

  const value = (claim.value ?? {}) as Record<string, unknown>;
  const signing = (claim.signing_channel ?? {}) as Record<string, unknown>;

  const description = typeof value.description === 'string' ? value.description : '';
  const channelName = typeof signing.name === 'string' ? signing.name : '';
  const channelClaimId = typeof signing.claim_id === 'string' ? signing.claim_id : '';
  const signatureValid = claim.is_channel_signature_valid === true;

  if (!channelName || !channelClaimId) return null;
  return { description, channelName, channelClaimId, signatureValid };
}

/**
 * Resolve the Odysee channel key ("@name#claimid") for a claim path, cached.
 * Requires a valid channel signature — an unsigned/invalidly-signed claim
 * has no trustworthy channel to bind to. Returns '' when undeterminable.
 */
export async function resolveOdyseeChannel(claimPath: string): Promise<string> {
  if (!claimPath) return '';
  return resolveCached(`odysee:${claimPath}`, async () => {
    const claim = await resolveOdyseeClaim(claimPath);
    if (!claim || !claim.signatureValid) return '';
    return odyseeChannelKey(claim.channelName, claim.channelClaimId);
  });
}

/**
 * Resolve the normalized channel key for a video, dispatching on provider.
 * `video` is anything carrying a verifiable `provider` and the provider id
 * (YouTube: 11-char id; Odysee: claim path) via `playback`.
 *
 * Returns { platform, channel } when resolvable, else null.
 */
export async function resolveVideoChannel(
  video: { provider: string; playback?: { id?: string } | null } | null | undefined,
): Promise<{ platform: VerifyPlatform; channel: string } | null> {
  const platform = video ? verifyPlatformForProvider(video.provider) : null;
  if (!platform) return null;
  const id = video?.playback?.id || '';
  if (!id) return null;

  const channel = platform === 'odysee'
    ? await resolveOdyseeChannel(id)
    : await resolveYouTubeChannel(id);
  return channel ? { platform, channel } : null;
}

// ── Control challenge ─────────────────────────────────────────────

export type VerifyCheckResult =
  | { ok: true; platform: VerifyPlatform; channel: string }
  | { ok: false; reason: string };

/**
 * Confirm control of a channel by reading back the token from the
 * description of one of the creator's own videos. Dispatches on the
 * provider of the pasted URL:
 *
 *   YouTube — read the channel from oEmbed, then substring-match the token
 *             in the watch-page HTML (oEmbed omits the description).
 *   Odysee  — read the channel and the description together from the LBRY
 *             `resolve` API, binding identity to the channel CLAIM ID.
 *
 * Best-effort, never throws, always goes through the read-only proxy.
 */
export async function checkVideoDescriptionToken(
  rawVideoUrl: string, pubkey: string,
): Promise<VerifyCheckResult> {
  const resolved = resolveVideoUrl(rawVideoUrl);
  const platform = resolved ? verifyPlatformForProvider(resolved.provider) : null;
  if (!resolved || !platform) {
    return { ok: false, reason: 'That link isn’t a YouTube or Odysee video. Verification supports those two today.' };
  }

  return platform === 'odysee'
    ? checkOdyseeToken(resolved.id, pubkey)
    : checkYouTubeToken(rawVideoUrl, pubkey);
}

/** YouTube branch: channel from oEmbed, token from watch-page HTML. */
async function checkYouTubeToken(rawVideoUrl: string, pubkey: string): Promise<VerifyCheckResult> {
  const url = parseVideoUrl(rawVideoUrl);
  if (!url) return { ok: false, reason: 'That doesn’t look like a valid YouTube video URL.' };

  // Extract the 11-char video id (watch?v=, youtu.be/…, /shorts/…).
  let videoId = '';
  const host = url.hostname.replace(/^www\./, '');
  if (host === 'youtu.be') videoId = url.pathname.slice(1).split('/')[0];
  else {
    videoId = url.searchParams.get('v') || '';
    if (!videoId) {
      const parts = url.pathname.split('/').filter(Boolean);
      const marker = parts.findIndex(p => ['shorts', 'embed', 'v', 'live'].includes(p));
      if (marker >= 0) videoId = parts[marker + 1] || '';
    }
  }
  if (!/^[A-Za-z0-9_-]{11}$/.test(videoId)) {
    return { ok: false, reason: 'Couldn’t read a YouTube video id from that link.' };
  }

  const channel = await resolveYouTubeChannel(videoId);
  if (!channel) {
    return { ok: false, reason: 'Couldn’t read that video’s channel. Try a different public video.' };
  }

  const token = verificationToken(pubkey);
  try {
    const watch = `https://www.youtube.com/watch?v=${videoId}`;
    const res = await fetch(`${PROXY}${encodeURIComponent(watch)}`, {
      signal: AbortSignal.timeout(15000),
    });
    if (!res.ok) return { ok: false, reason: 'Couldn’t reach YouTube to check. Try again in a moment.' };
    const html = await res.text();
    // The description appears in the page's JSON blob, HTML-escaped. The
    // token is plain ASCII (npub + prefix), so a substring test is robust
    // to escaping without parsing YouTube's markup.
    if (html.includes(token)) return { ok: true, platform: 'youtube', channel };
    return {
      ok: false,
      reason: 'Token not found in that video’s description yet. Paste it in, save, then wait a minute and retry.',
    };
  } catch {
    return { ok: false, reason: 'Couldn’t reach YouTube to check. Try again in a moment.' };
  }
}

/** Odysee branch: channel + description together from the resolve API. */
async function checkOdyseeToken(claimPath: string, pubkey: string): Promise<VerifyCheckResult> {
  let claim: OdyseeClaim | null = null;
  try {
    claim = await resolveOdyseeClaim(claimPath);
  } catch {
    return { ok: false, reason: 'Couldn’t reach Odysee to check. Try again in a moment.' };
  }
  if (!claim) {
    return { ok: false, reason: 'Couldn’t read that Odysee video. Try a different public video from your channel.' };
  }
  if (!claim.signatureValid) {
    return { ok: false, reason: 'That Odysee video isn’t signed by a channel, so it can’t prove channel control. Post it under your channel.' };
  }

  const channel = odyseeChannelKey(claim.channelName, claim.channelClaimId);
  if (!channel) {
    return { ok: false, reason: 'Couldn’t read that video’s channel claim id. Try a different public video.' };
  }

  const token = verificationToken(pubkey);
  if (claim.description.includes(token)) {
    return { ok: true, platform: 'odysee', channel };
  }
  return {
    ok: false,
    reason: 'Token not found in that video’s description yet. Paste it in, save, then wait a minute and retry.',
  };
}
